import React from "react";
import Layout from "@/components/utu/Layout";
import { GlassCard } from "@/components/utu/atoms";
import { SEO } from "@/components/SEO";
import { Terminal, ShieldAlert, Cpu, BookOpen, Layers } from "lucide-react";

export default function McpDocs() {
  const codeSnippet = `{
  "jsonrpc": "2.0",
  "method": "tools/call",
  "params": {
    "name": "utu_linguistic_stabilizer",
    "arguments": {
      "text": "Niko kwa meeting, nitacall back later.",
      "language_hints": ["sw", "en", "sheng"]
    }
  },
  "id": 1
}`;

  const responseSnippet = `{
  "jsonrpc": "2.0",
  "result": {
    "content": [
      {
        "type": "text",
        "text": "I am in a meeting, I will call you back later."
      }
    ],
    "metadata": {
      "dialect": "Sheng-Urban",
      "tokens_original": 14,
      "tokens_optimized": 8,
      "injected_context_nodes": [
        "sheng_meeting_helper_verb",
        "kenyan_workplace_etiquette"
      ]
    }
  },
  "id": 1
}`;

  return (
    <Layout>
      <SEO
        title="UTU MCP Developer Documentation"
        description="Integrate the UTU Model Context Protocol (MCP) server into your OpenAI, Anthropic, Mistral, or Llama pipelines. View API references, SDK quick start, and language guidelines."
        url="/mcp/docs"
      />
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        <div className="grid gap-10 md:grid-cols-[220px_1fr] items-start">
          {/* Side Nav */}
          <aside className="sticky top-24 hidden md:block space-y-4 text-xs font-semibold text-white/50">
            <div className="text-[10px] tracking-wider uppercase text-white/30 font-bold">API REFERENCE</div>
            <a href="#quickstart" className="block text-white hover:text-[hsl(var(--utu-gold))] transition-colors">Quick Start</a>
            <a href="#payloads" className="block hover:text-[hsl(var(--utu-gold))] transition-colors">Request & Response</a>
            <a href="#integrations" className="block hover:text-[hsl(var(--utu-gold))] transition-colors">Framework Integrations</a>
            <a href="#languages" className="block hover:text-[hsl(var(--utu-gold))] transition-colors">Supported Languages</a>
            <a href="#limits" className="block hover:text-[hsl(var(--utu-gold))] transition-colors">Rate Limits & Access</a>
          </aside>

          {/* Doc Content */}
          <div className="space-y-12">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-[hsl(var(--utu-gold))] backdrop-blur-xl mb-4">
                <BookOpen className="h-4 w-4" />
                Developer Docs
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-4">
                Model Context Protocol (MCP) API Reference
              </h1>
              <p className="text-base text-white/70 leading-relaxed">
                Connect the UTU context layer directly to your AI assistants. UTU implements the Model Context Protocol, exposing tool calling interfaces compatible with major SDKs.
              </p>
            </div>

            {/* Quickstart */}
            <div id="quickstart" className="scroll-mt-24 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2 border-b border-white/10 pb-2">
                <Terminal className="h-5 w-5 text-[hsl(var(--utu-gold))]" />
                Quick Start
              </h2>
              <p className="text-sm text-white/70">
                To connect to our self-hosted or cloud-hosted servers, add the UTU MCP Server config configuration block to your Claude Desktop or system config:
              </p>
              <GlassCard className="p-4 bg-black/50 border-white/5 font-mono text-xs overflow-x-auto">
                <pre className="text-white/80">{`{
  "mcpServers": {
    "utu-context-stabilizer": {
      "command": "npx",
      "args": ["-y", "@utuafrica/mcp-server@latest"],
      "env": {
        "UTU_API_KEY": "your_secure_auth_token_here"
      }
    }
  }
}`}</pre>
              </GlassCard>
            </div>

            {/* Payloads */}
            <div id="payloads" className="scroll-mt-24 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2 border-b border-white/10 pb-2">
                <Layers className="h-5 w-5 text-[hsl(var(--utu-gold))]" />
                Request & Response
              </h2>
              <p className="text-sm text-white/70">
                Execute prompt optimization and context injection by calling the `utu_linguistic_stabilizer` tool endpoint:
              </p>

              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <div className="text-[10px] text-white/50 font-bold uppercase mb-2">Request Payload</div>
                  <GlassCard className="p-4 bg-black/50 border-white/5 font-mono text-xs overflow-x-auto">
                    <pre className="text-emerald-200/80">{codeSnippet}</pre>
                  </GlassCard>
                </div>
                <div>
                  <div className="text-[10px] text-white/50 font-bold uppercase mb-2">Response JSON</div>
                  <GlassCard className="p-4 bg-black/50 border-white/5 font-mono text-xs overflow-x-auto">
                    <pre className="text-amber-200/80">{responseSnippet}</pre>
                  </GlassCard>
                </div>
              </div>
            </div>

            {/* Integrations */}
            <div id="integrations" className="scroll-mt-24 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2 border-b border-white/10 pb-2">
                <Cpu className="h-5 w-5 text-[hsl(var(--utu-gold))]" />
                Framework Integrations
              </h2>
              <p className="text-sm text-white/70">
                Because UTU adheres strictly to the MCP standard specification, it integrates out of the box with your existing AI pipelines.
              </p>
              <div className="grid gap-4 sm:grid-cols-2 text-xs">
                <div className="p-4 bg-white/[0.02] border border-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">LangChain & LangGraph</h4>
                  <p className="text-white/60">Configure standard MCP tools to be invoked in LangChain agents with our standard connector packages.</p>
                </div>
                <div className="p-4 bg-white/[0.02] border border-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">Vercel AI SDK</h4>
                  <p className="text-white/60">Use the `custom tool` configuration to route incoming messaging streams to the UTU endpoint first.</p>
                </div>
                <div className="p-4 bg-white/[0.02] border border-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">OpenAI Assistant API</h4>
                  <p className="text-white/60">Upload UTU function models directly into assistant schema files to enable automatic mapping.</p>
                </div>
                <div className="p-4 bg-white/[0.02] border border-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">LlamaIndex</h4>
                  <p className="text-white/60">Connect the UTU vector data layer directly to indexing wrappers as a custom document reader.</p>
                </div>
              </div>
            </div>

            {/* Languages */}
            <div id="languages" className="scroll-mt-24 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2 border-b border-white/10 pb-2">
                Supported Dialects & Languages
              </h2>
              <p className="text-sm text-white/70">
                UTU supports over 61 languages and regional code-mixed variations. Key clusters include:
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                {["Swahili (Standard)", "Swahili (Congolese)", "Sheng (Nairobi)", "Kikuyu", "Luo", "Luhya", "Kalenjin", "Luganda", "Zulu", "Yoruba", "Igbo", "Oromo"].map(l => (
                  <span key={l} className="px-3 py-1 bg-white/5 border border-white/10 text-white/80 rounded-full">
                    {l}
                  </span>
                ))}
              </div>
            </div>

            {/* Limits */}
            <div id="limits" className="scroll-mt-24 space-y-4">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2 border-b border-white/10 pb-2">
                <ShieldAlert className="h-5 w-5 text-[hsl(var(--utu-gold))]" />
                Rate Limits & Access Tiers
              </h2>
              <div className="overflow-hidden border border-white/10 rounded-xl bg-white/[0.02] text-xs">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="p-4 font-semibold text-white">Tier</th>
                      <th className="p-4 font-semibold text-white">Limits</th>
                      <th className="p-4 font-semibold text-white">Integration Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="p-4 font-semibold text-white">Developer (Waitlist)</td>
                      <td className="p-4 text-white/70">1,000 queries / day</td>
                      <td className="p-4 text-white/70">Local host only, community support</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="p-4 font-semibold text-white">Growth / Startup</td>
                      <td className="p-4 text-white/70">50,000 queries / day</td>
                      <td className="p-4 text-white/70">Cloud cluster host, API, standard security</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-semibold text-white">Enterprise</td>
                      <td className="p-4 text-white/70">Custom scale limits</td>
                      <td className="p-4 text-white/70">Self-hosted private VPN, SLA, priority support</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
